import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { AnimatePresence } from 'framer-motion';
import { DropletIcon, EyeIcon, EyeOffIcon, HistoryIcon, MoreVerticalIcon, PanelLeftIcon, SearchIcon } from 'lucide-react';
import { MenuButton } from '../ui/MenuButton';
import { ExportMeshModal } from '../export/ExportMeshModal';
import { getSelectedPart, getViewportBridge, setPartNames, setSelectedPart, subscribeViewportBridge } from '../../utils/viewportBridge';
import { exportBuildFile, saveBlob } from '../../utils/meshExport';
import { useViewportModelReady } from '../../hooks/useViewportModelReady';
type ProjectSidebarProps = {
  historyOpen: boolean;
  onToggleHistory: () => void;
};
type Part = {
  id: string;
  name: string;
  visible: boolean;
};
const BUILD_NAME = 'Mini Chassis Kit';
function copyName(name: string, existing: Part[]) {
  const root = name.replace(/ \(copy(?: \d+)?\)$/, '');
  for (let n = 1;; n++) {
    const candidate = n === 1 ? `${root} (copy)` : `${root} (copy ${n})`;
    if (!existing.some(p => p.name === candidate)) return candidate;
  }
}
export function ProjectSidebar({
  historyOpen,
  onToggleHistory
}: ProjectSidebarProps) {
  const [expanded, setExpanded] = useState(true);
  const [parts, setParts] = useState<Part[]>([{
    id: 'main',
    name: BUILD_NAME,
    visible: true
  }]);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [exportPart, setExportPart] = useState<Part | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<number>();
  const ready = useViewportModelReady();
  const selectedId = useSyncExternalStore(subscribeViewportBridge, getSelectedPart);
  const historyPartId = selectedId ?? 'main';
  useEffect(() => {
    setPartNames(Object.fromEntries(parts.map(p => [p.id, p.name])));
  }, [parts]);
  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);
  const flash = (text: string) => {
    setNotice(text);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 3500);
  };
  const update = (id: string, patch: Partial<Part>) => setParts(ps => ps.map(p => p.id === id ? {
    ...p,
    ...patch
  } : p));
  const toggleVisible = (p: Part) => {
    getViewportBridge()?.setVisible(p.id, !p.visible);
    update(p.id, {
      visible: !p.visible
    });
    if (p.visible && selectedId === p.id) setSelectedPart(null);
  };
  const duplicate = (p: Part) => {
    const id = getViewportBridge()?.duplicate();
    if (!id) return;
    setParts(ps => [...ps, {
      id,
      name: copyName(p.name, ps),
      visible: true
    }]);
    setSelectedPart(id);
  };
  const startRename = (p: Part) => {
    setDraft(p.name);
    setRenamingId(p.id);
  };
  const commitRename = () => {
    const name = draft.trim();
    if (renamingId && name) update(renamingId, {
      name
    });
    setRenamingId(null);
  };
  const exportBuild = async () => {
    const model = getViewportBridge()?.getModel();
    if (!model) return;
    flash('Exporting build file…');
    try {
      const blob = await exportBuildFile(model, {
        app: 'PreForm',
        name: BUILD_NAME,
        printer: 'Form 3',
        material: 'Alumina 4N V1',
        layerThickness: '0.050 mm',
        parts: parts.map(p => ({
          name: p.name,
          visible: p.visible
        })),
        exportedAt: new Date().toISOString()
      });
      await saveBlob(blob, `${BUILD_NAME}.form`);
      flash(`Saved ${BUILD_NAME}.form`);
    } catch {
      flash('Couldn’t export the build file');
    }
  };
  return <aside aria-label="Project" className="flex w-[232px] shrink-0 flex-col border-r border-pf-line bg-white" data-id="element-21" data-mp="2qb0yojnca0gm">
      <div className="flex h-[38px] items-center justify-between px-[12px]" data-id="element-22" data-mp="2vadvnx7xcnwx">
        <h1 className="text-[13.5px] font-bold text-pf-ink" data-id="element-23" data-mp="vwqb0eihdccf">Untitled Project</h1>
        <div className="flex items-center gap-[18px] text-[#4b5056]" data-id="element-24" data-mp="vyuptrhx3vrh">
          <button type="button" aria-label="Search models" className="hover:text-pf-ink" data-id="element-25" data-mp="10wmhckenhfv5">
            <SearchIcon className="h-[12px] w-[12px]" strokeWidth={2.2} data-id="element-26" data-mp="2rbnbhv630z5h" />
          </button>
          <button type="button" aria-label="Collapse sidebar" className="hover:text-pf-ink" data-id="element-27" data-mp="2mcsjq3q50098">
            <PanelLeftIcon className="h-[12px] w-[12px]" strokeWidth={2.2} data-id="element-28" data-mp="30dwi49y8c8dl" />
          </button>
        </div>
      </div>
      <div className="border-b border-pf-line px-[12px] pb-[7px] pt-[4px]" data-id="element-29" data-mp="2wdckz6dnp4rg">
        <button type="button" className="h-[23px] w-full rounded-full border border-pf-blue-line text-[12px] text-pf-blue transition-colors duration-150 hover:bg-pf-blue-soft" data-id="element-30" data-mp="28lnt1r4qyib1">
          New Build
        </button>
      </div>

      <div className="flex items-center gap-[8px] bg-[#e8eef6] pr-[8px]" data-id="element-31" data-mp="2hgbt92vgt9sz">
        <button type="button" aria-expanded={expanded} onClick={() => setExpanded(e => !e)} className="flex min-w-0 flex-1 items-center gap-[6px] py-[7px] pl-[8px] text-left" data-id="element-32" data-mp="2cmzjfyvg37ka">
          <svg viewBox="0 0 8 5" className={`h-[4px] w-[7px] shrink-0 text-pf-ink transition-transform duration-150 ${expanded ? '' : '-rotate-90'}`} fill="currentColor" aria-hidden="true" data-id="element-33" data-mp="1kxe9kvv0a6n2">
            <path d="M0 0 H8 L4 5 Z" data-id="element-34" data-mp="1fje8pr0zwj7q" />
          </svg>
          <span className="min-w-0 flex-1 pl-[5px]" data-id="element-35" data-mp="1fl7fd6blz3fq">
            <span className="block truncate text-[12px] font-bold text-pf-ink" title={BUILD_NAME} data-id="element-36" data-mp="10v9ylbg0dg0k">
              {BUILD_NAME}
            </span>
            <span className="block text-[9.5px] text-[#4b5056]" data-id="element-37" data-mp="15ucwmbxkmlz1">
              {parts.length} {parts.length === 1 ? 'model' : 'models'}
            </span>
          </span>
        </button>
        <button type="button" aria-label={historyOpen ? 'Close history' : 'Open history'} aria-pressed={historyOpen} title="History" onClick={onToggleHistory} className={`flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[6px] border transition-colors duration-150 ${historyOpen ? 'border-pf-blue bg-pf-blue text-white' : 'border-[#c4c9cf] bg-white text-[#4b5056] hover:border-[#a9afb6] hover:text-pf-ink'}`} data-id="element-38" data-mp="1yh7dyzz3d58a">
          
          <HistoryIcon className="h-[13px] w-[13px]" strokeWidth={2.2} data-id="element-39" data-mp="2fr1kec7mi0e5" />
        </button>
      </div>
      {expanded && <div className="flex h-[24px] items-center justify-between bg-[#5b636c] px-[12px] text-white" data-id="element-40" data-mp="2av42w2kminzs">
          <span className="flex items-center gap-[4px] text-[9.5px] font-extrabold tracking-wide" data-id="element-41" data-mp="212y86l2zlsgw">
            FORM <span className="rounded-[3px] bg-white px-[3px] py-[1px] text-[#2f353c]" data-id="element-42" data-mp="1w3lo283i2zi0">3/3+</span>
          </span>
          <span className="flex items-center gap-[4px] text-[9.5px] font-extrabold tracking-wide" data-id="element-43" data-mp="1s1qmr3lkkm48">
            ALUMINA 4N <DropletIcon className="h-[10px] w-[10px] text-[#e8c9c9]" strokeWidth={1.8} data-id="element-44" data-mp="1n2dshxsdccrm" />
          </span>
        </div>}
      {expanded && <ul aria-label="Models in build" data-id="element-45" data-mp="1i66uqsa4m7kr">
          {parts.map(p => <li key={p.id} className={`flex h-[26px] items-center gap-[7px] pl-[12px] pr-[4px] transition-colors duration-150 ${selectedId === p.id ? 'bg-[#d3e3f7]' : historyOpen && p.id === historyPartId ? 'bg-pf-blue-soft' : 'bg-[#f2f6fb] hover:bg-[#e9f0f9]'} ${historyOpen && p.id === historyPartId ? 'border-l-[3px] border-pf-blue pl-[9px]' : ''}`} data-id="element-46" data-mp="3hfvhlp8f9hor">
          
              <button type="button" aria-label={p.visible ? `Hide ${p.name}` : `Show ${p.name}`} aria-pressed={!p.visible} onClick={() => toggleVisible(p)} className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[4px] transition-colors duration-150 hover:bg-black/5 ${p.visible ? 'text-pf-ink' : 'text-pf-faint'}`} data-id="element-47" data-mp="3ts7mvx96h1x6">
            
                {p.visible ? <EyeIcon className="h-[13px] w-[13px]" strokeWidth={2.4} data-id="element-48" data-mp="3b3aopiantp9o" /> : <EyeOffIcon className="h-[13px] w-[13px]" strokeWidth={2.2} data-id="element-49" data-mp="3g1m1o1w94fw4" />}
              </button>
              {renamingId === p.id ? <input autoFocus aria-label="Model name" value={draft} onChange={e => setDraft(e.target.value)} onFocus={e => e.target.select()} onBlur={commitRename} onKeyDown={e => {
          if (e.key === 'Enter') commitRename();
          if (e.key === 'Escape') setRenamingId(null);
        }} className="h-[20px] min-w-0 flex-1 rounded-[3px] border border-pf-blue-line bg-white px-[4px] text-[12px] text-pf-ink outline-none" data-id="element-50" data-mp="qd1t3rw1ll4j" /> : <button type="button" onClick={() => p.visible && setSelectedPart(selectedId === p.id ? null : p.id)} onDoubleClick={() => startRename(p)} aria-pressed={selectedId === p.id} className={`h-full min-w-0 flex-1 truncate text-left text-[12px] ${p.visible ? 'text-pf-ink' : 'cursor-default text-pf-faint'} ${selectedId === p.id ? 'font-semibold' : ''}`} title={p.name} data-id="element-51" data-mp="1z3atwo3onl0e">
            
                  {p.name}
                </button>}
              <MenuButton label={`Actions for ${p.name}`} icon={<MoreVerticalIcon className="h-[13px] w-[13px]" strokeWidth={2.4} data-id="element-53" data-mp="2ldsome926iws" />} items={[{
          label: 'Duplicate',
          onSelect: () => duplicate(p),
          disabled: !ready
        }, {
          label: 'Rename',
          onSelect: () => startRename(p)
        }, {
          label: 'Export Build File…',
          onSelect: exportBuild,
          disabled: !ready
        }, {
          label: 'Export Mesh…',
          onSelect: () => setExportPart(p),
          disabled: !ready
        }]} data-id="element-52" data-mp="9r8p2mhzgikl" />
          
            </li>)}
        </ul>}
      {notice && <p role="status" className="px-[12px] pt-[6px] text-[11px] text-pf-muted" data-id="element-54" data-mp="27mxllil8qw14">
          {notice}
        </p>}

      <AnimatePresence data-id="element-55" data-mp="1sq6o5ev4nue5">{exportPart && <ExportMeshModal key="export-mesh" partName={exportPart.name} onClose={() => setExportPart(null)} data-id="element-56" data-mp="1xmx3u130snz1" />}</AnimatePresence>
    </aside>;
}